import { useMutation } from '@apollo/client';
import noop from 'lodash/noop';
import { useEffect, useRef, useState } from 'react';

import { uploadUserFileToGcs, useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Caption,
  Heading,
  Icon,
  Input,
  LoadingIcon,
  Stack,
  Text,
  cn,
  useToast,
} from '@eluve/components';
import { EluveEditor, EluveEditorHandle } from '@eluve/eluve-editor';
import { UserFileTypesLookup } from '@eluve/graphql-types';
import { useAnalytics } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { MAX_PDF_UPLOAD_BYTES } from '@eluve/utils';

import { PdfPreview } from '../PdfPreview';
import {
  insertDocumentReferenceTemplateMutation,
  updateDocumentReferenceTemplateMutation,
} from '../documents.operations';
import { useSignedUrl } from '../useSignedUrl';

type PdfUploadZoneProps = {
  inputRef: React.RefObject<HTMLInputElement | null>;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isDragging: boolean;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  selectedFileName: string | null;
};

const PdfUploadZone: React.FC<PdfUploadZoneProps> = ({
  inputRef,
  onFileSelect,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  selectedFileName,
}) => (
  <>
    <Input
      ref={inputRef}
      type="file"
      accept=".pdf"
      className="hidden"
      onChange={onFileSelect}
    />
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={cn(
        'flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center transition-colors',
        isDragging
          ? 'border-border-indigo-strong bg-bg-indigo-subtle-primary/30'
          : 'border-border-primary hover:bg-bg-tertiary',
      )}
    >
      <Icon name="Upload" size="xl" className="text-text-tertiary" />
      <Text size="sm" color="secondary">
        {isDragging
          ? 'Drop your PDF here'
          : (selectedFileName ??
            'Click to browse, or drag and drop your PDF here')}
      </Text>
      <Text size="xs" color="tertiary">
        PDF only, max 10 MB
      </Text>
    </button>
  </>
);

export type CreateTemplateFormProps = {
  onBack: () => void;
  onSaved: () => void;
  mode: 'note' | 'pdf_form';
  template?: {
    id: string;
    name: string;
    content: string;
    kind?: string;
    sourceFileId?: string | null;
  } | null;
};

export const CreateTemplateForm: React.FC<CreateTemplateFormProps> = ({
  onBack,
  onSaved,
  mode,
  template = null,
}) => {
  const isEditMode = template !== null;
  const isPdfEdit = isEditMode && template.kind === 'PDF_FORM';
  const { capture } = useAnalytics();
  const toast = useToast();
  const editorRef = useRef<EluveEditorHandle | null>(null);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [uploadState, setUploadState] = useState<
    'idle' | 'uploading' | 'processing' | 'error'
  >('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [editorRevision, setEditorRevision] = useState(0);
  const [generatedInstructions, setGeneratedInstructions] = useState<
    string | null
  >(null);
  const [generatedDocType, setGeneratedDocType] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [exampleUploadId, setExampleUploadId] = useState<string | null>(null);
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams();
  // Presigned GCS URLs only — pdf.js fetches via CSP connect-src, which allows
  // storage.googleapis.com but not blob:.
  const { url: editPreviewUrl } = useSignedUrl(
    isPdfEdit ? template?.sourceFileId : null,
  );
  const { url: examplePreviewUrl } = useSignedUrl(exampleUploadId);

  const [insertTemplate] = useMutation(insertDocumentReferenceTemplateMutation);
  const [updateTemplate] = useMutation(updateDocumentReferenceTemplateMutation);

  useEffect(() => {
    setName(template?.name ?? '');
    setUploadState('idle');
    setUploadError(null);
    setSelectedFileName(null);
    setEditorContent(template?.content ?? '');
    setEditorRevision(0);
    setGeneratedInstructions(null);
    setGeneratedDocType(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [template]);

  const handleSave = async () => {
    const content = editorRef.current?.getMarkdown() ?? '';
    if (!name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!isPdfEdit && !content.trim()) {
      toast.error('Please enter template content');
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateTemplate({
          variables: {
            id: template.id,
            set: isPdfEdit
              ? { name: name.trim() }
              : { name: name.trim(), content },
          },
        });
        capture('template_updated', { template_id: template.id });
        toast.success('Template updated');
      } else {
        const result = await insertTemplate({
          variables: {
            object: {
              name: name.trim(),
              content,
              ...(generatedInstructions && {
                instructions: generatedInstructions,
              }),
              ...(generatedDocType && { docType: generatedDocType }),
            },
          },
        });
        const templateId = result.data?.insertDocumentReferenceTemplatesOne?.id;
        if (templateId) {
          capture('template_created', { template_id: templateId });
        }
        toast.success('Template created');
      }

      setName('');
      onSaved();
    } catch {
      toast.error(
        isEditMode ? 'Failed to update template' : 'Failed to create template',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const uploadPdfFile = (
    file: File,
    fileType: (typeof UserFileTypesLookup)[keyof typeof UserFileTypesLookup],
  ): Promise<string> =>
    uploadUserFileToGcs({ apiClient, tenantId: tenantId!, file, fileType });

  const beginPdfUpload = (file: File): boolean => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return false;
    }
    if (file.size > MAX_PDF_UPLOAD_BYTES) {
      toast.error('File size must be less than 10 MB');
      return false;
    }
    if (!tenantId) {
      toast.error('Unable to determine tenant');
      return false;
    }
    setSelectedFileName(file.name);
    setUploadState('uploading');
    setUploadError(null);
    return true;
  };

  const processFile = async (file: File) => {
    if (!beginPdfUpload(file) || !tenantId) return;
    setExampleUploadId(null);

    try {
      const userUploadId = await uploadPdfFile(
        file,
        UserFileTypesLookup.CHAT_DOCUMENT,
      );
      setExampleUploadId(userUploadId);

      setUploadState('processing');

      const generateResponse = await apiClient.documents.generateTemplate({
        params: { tenantId },
        body: { userUploadId },
      });

      if (generateResponse.status !== 200) {
        throw new Error('Failed to generate template');
      }

      const result = generateResponse.body;

      if (result.status === 'rejected') {
        setUploadState('error');
        setUploadError(result.reason);
        return;
      }

      const existing = editorRef.current?.getMarkdown() ?? editorContent;
      const merged = [existing.trim(), result.content.trim()]
        .filter(Boolean)
        .join('\n\n');
      setEditorContent(merged);
      setEditorRevision((r) => r + 1);
      if (!name.trim()) {
        setName(result.name);
      }
      setGeneratedInstructions(result.instructions);
      setGeneratedDocType(result.docType ?? null);
      setSelectedFileName(null);
      setExampleUploadId(null);
      setUploadState('idle');
    } catch (error) {
      setUploadState('error');
      const message =
        error instanceof Error ? error.message : 'Something went wrong';
      setUploadError(message);
      toast.error(`Template generation failed: ${message}`);
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (file) void processFile(file);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) void processFile(file);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  };

  return (
    <Stack gap={6} className="max-h-[85vh] overflow-y-auto p-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="-ml-1 self-start text-text-secondary"
      >
        <Icon name="ArrowLeft" size="auto" />
        Back to templates
      </Button>
      {!isEditMode && (
        <Stack gap={1} className="pr-8">
          <Heading size="lg">Create a new template</Heading>
          <Text size="xs" color="tertiary" className="tracking-[-0.14px]">
            Generate it from an AI prompt — describe the document this template
            should produce. For example, “a referral letter to a cardiologist
            noting the patient’s chest pain and history.”
          </Text>
        </Stack>
      )}

      {isEditMode && (
        <Stack gap={1} className="pr-8">
          <Heading size="lg">Edit template</Heading>
          <Text size="xs" color="tertiary" className="tracking-[-0.14px]">
            {isPdfEdit
              ? 'Rename this PDF form template.'
              : 'Update your template content and name.'}
          </Text>
        </Stack>
      )}

      {mode === 'note' && !isPdfEdit && (
        <div className="h-[300px] w-full overflow-y-auto rounded-lg border border-border-primary px-3 py-2.5 shadow-sm">
          <EluveEditor
            key={`${template?.id ?? 'new'}-${editorRevision}`}
            ref={editorRef}
            content={editorContent}
            onUpdate={noop}
          />
        </div>
      )}

      {isPdfEdit && editPreviewUrl && (
        <PdfPreview
          file={editPreviewUrl}
          className="max-h-[50vh]"
          highlightFields
        />
      )}

      {mode === 'note' && !isEditMode && (
        <>
          <Stack gap={6} align="center" direction="horizontal">
            <div className="h-px flex-1 bg-border-primary" />
            <Caption inline size="2xs" weight="bold" color="tertiary">
              AND
            </Caption>
            <div className="h-px flex-1 bg-border-primary" />
          </Stack>

          <Stack gap={3}>
            <Stack gap={1}>
              <Text className="tracking-[-0.14px]">
                Add an example to start from (optional)
              </Text>
              <Text color="tertiary" className="tracking-[-0.14px]">
                Upload an example document — Eluve turns it into template
                content and adds it to what you&apos;ve written.
              </Text>
            </Stack>

            {uploadState === 'error' && uploadError && (
              <Stack
                gap={1}
                className="rounded-lg border border-border-negative-subtle bg-bg-negative-subtle px-3 py-2"
              >
                <Text size="xs" className="text-text-negative">
                  {uploadError}
                </Text>
              </Stack>
            )}

            {uploadState === 'uploading' || uploadState === 'processing' ? (
              <Stack
                gap={3}
                align="center"
                className="rounded-lg border border-border-primary p-3"
                direction="horizontal"
              >
                <LoadingIcon size="lg" />
                <Text inline size="xs" color="secondary">
                  {uploadState === 'uploading'
                    ? `Uploading ${selectedFileName ?? 'file'}...`
                    : 'Generating template from document...'}
                </Text>
              </Stack>
            ) : (
              <PdfUploadZone
                inputRef={fileInputRef}
                onFileSelect={handleFileInputChange}
                isDragging={isDragging}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                selectedFileName={selectedFileName}
              />
            )}

            {examplePreviewUrl && (
              <PdfPreview file={examplePreviewUrl} className="max-h-[280px]" />
            )}
          </Stack>
        </>
      )}

      <Stack gap={1}>
        <Stack gap={2} align="center" direction="horizontal">
          <Text size="xs" color="secondary">
            Template name
          </Text>
          {!name.trim() && (
            <Text inline size="xs" className="text-text-negative">
              Required
            </Text>
          )}
        </Stack>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name your template"
        />
      </Stack>

      <Stack justify="between" align="center" gap={2} direction="horizontal">
        <Button variant="outline" onClick={onBack}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            isSaving ||
            !name.trim() ||
            uploadState === 'uploading' ||
            uploadState === 'processing'
          }
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );
};
